import { ChevronsUpDown, LogOut, UserRound } from 'lucide-react'
import { followLink } from '@web/lib/navigation'
import { ROUTES } from '@web/router'
import { Avatar, AvatarFallback } from '@layerit/ui/components/ui/avatar'
import { Button } from '@layerit/ui/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@layerit/ui/components/ui/dropdown-menu'

export interface AccountIdentity {
	email: string
	displayName?: string | null
	name?: string | null
}

interface ProfileMenuProps {
	user: AccountIdentity
	signOut: () => Promise<void>
	navigate: (to: string) => void
}

export function initialsOf(label: string): string {
	return (
		label
			.split(/[\s@._-]+/)
			.filter(Boolean)
			.slice(0, 2)
			.map((part) => (part[0] ?? '').toUpperCase())
			.join('') || '?'
	)
}

export function ProfileMenu({ user, signOut, navigate }: ProfileMenuProps) {
	const email = user.email
	const named = typeof user.displayName === 'string' && user.displayName.trim() !== '' ? user.displayName : user.name
	const displayName = typeof named === 'string' && named.trim() !== '' ? named : null

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				render={
					<Button
						aria-label={`Account: signed in as ${email}`}
						className="h-9 max-w-16 gap-2 px-1.5 sm:max-w-[16rem] sm:pr-2"
						variant="ghost"
					/>
				}
			>
				<Avatar size="sm">
					<AvatarFallback>{initialsOf(displayName ?? email)}</AvatarFallback>
				</Avatar>
				<span className="hidden truncate text-sm font-normal text-ink-2 sm:inline">{email}</span>
				<ChevronsUpDown className="hidden text-ink-3 sm:inline" />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" side="bottom" className="min-w-60">
				<DropdownMenuGroup>
					<DropdownMenuLabel className="flex flex-col gap-0.5 py-1.5">
						<span className="truncate text-sm font-medium text-ink">{displayName ?? 'Your account'}</span>
						<span className="truncate font-mono text-xs font-normal text-ink-3">{email}</span>
					</DropdownMenuLabel>
					<DropdownMenuSeparator />
					<DropdownMenuItem
						render={
							<a href={ROUTES.settingsAccount} onClick={(event) => followLink(event, ROUTES.settingsAccount, navigate)} />
						}
					>
						<UserRound />
						My Account
					</DropdownMenuItem>
					<DropdownMenuItem variant="destructive" onClick={() => void signOut()}>
						<LogOut />
						Sign out
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
